import axios from 'axios'
import appSettings from '@/app-settings.json'
import themeSettings from '@/theme-settings.json'

/**
 * Concrete implementation of claims apis
 *
 * should be considered private, use Api to access
 **/
export default {

  /**
   * Post a claim to the API
   * @param {DamageClaim} damageClaim - claim object to post
   * @returns {Promise<ClaimResult>} - A promise containing a ClaimResult
   */
  postClaim (damageClaim) {
    // set the correct claim proivider id (easyjet, damagedluggage, etc)
    damageClaim.airlineCode = themeSettings.airlineCode
    damageClaim.claimProviderId = themeSettings.claimProviderId
    return axios
      .post(
        appSettings.endpoints.claims.submit,
        damageClaim,
        {
          headers: this.getBaseHeaders()
        })
      .then((results) => { return new ClaimResult(results.data) })
  },

  /**
   * Get status of the user's claim
   * @param {string} guid - the guid for the claim
   */
  getClaimStatus (guid, code) {
    return axios.get(`${appSettings.endpoints.claims.submit}/${guid}`, {
      headers: this.getBaseHeaders(guid, code)
    })
  },

  /**
   * Get the chat messages connected to the specified claim
   * @param {string} guid - the guid for the claim
   * @returns {Promise<ClaimMessage[]>} - A promise containing array of ClaimMessage
   */
  getClaimMessages (guid, code) {
    return axios.get(`${appSettings.endpoints.claims.submit}/${guid}/messages`, {
      headers: this.getBaseHeaders(guid, code)
    }).then((results) => {
      if (results && results.data && results.data.length > 0) {
        return results.data.map(message => {
          return new ClaimMessage(
            message
          )
        })
      } else {
        return []
      }
    })
  },

  getClaimVerificationCode (guid) {
    return axios.get(`${appSettings.endpoints.claims.submit}/verificationcode/${guid}`, {
      headers: this.getBaseHeaders(guid)
    })
  },

  /**
   * Add a new chat message to a claim
   * @param {string} guid - guid for the claim
   * @param {stirng} message - chat message to add to the claim
   */
  postClaimMessage (guid, code, message) {
    return axios
      .post(
        `${appSettings.endpoints.claims.submit}/${guid}/messages`,
        {
          text: message
        },
        {
          headers: this.getBaseHeaders(guid, code)
        })
  },

  /**
   * Add a new document to a claim
   * @param {string} guid - guid for the claim
   * @param {string} code - the verification code for the claim
   * @param {stirng} fileUrl - uploaded file url
   * @param {stirng} description - uploaded file description e.g. filename
   */
  postClaimDocument (guid, code, fileUrl, description) {
    return axios
      .post(
        `${appSettings.endpoints.claims.submit}/${guid}/documents`,
        {
          url: fileUrl,
          description: description
        },
        {
          headers: this.getBaseHeaders(guid, code)
        })
  },

  /**
   * Get reported claim details from the PIR number.
   * i.e. get the claim details from WorldTracer
   * @param {string} pir - pir reference
   */
  getAirportReport (pir) {
    return axios.post(
      appSettings.endpoints.airportReport,
      {
        code: pir
      },
      {
        headers: this.getBaseHeaders()
      })
  },

  /**
   * Post images update object to the API
   * @param {string} guid - guid of the user
   * @param {imageUpdate} imageUpdate - object containing images to be updated
   */
  postClaimImageUpdate (guid, imageUpdate, baggageItemId) {
    return axios
      .post(
        `${appSettings.endpoints.claims.submit}/${guid}/updatephotos/${baggageItemId}`,
        imageUpdate,
        {
          headers: this.getBaseHeaders()
        })
      .then((results) => { return results })
  },

  /**
   * Posts details of the baggage items to get suitable replacement options
   * from the API (requires flight date and claim provider)
   * @param {StockReplacementRequest} replacementRequest - request payload containing baggageItem and claim info
   */
  getReplacementOptions (replacementRequest) {
    return axios.post(
      appSettings.endpoints.replacements,
      replacementRequest,
      {
        headers: this.getBaseHeaders()
      })
  },

  /**
   * Posts user's decision on the replacement items he has been offered
   * @param {String} guid - unique identifier of the claim
   * @param {ReplacementStatusUpdate} replacementStatus - object containing user response
   *                                                      regards to his replacement offer
   */
  updateClaimReplacementStatus (guid, code, replacementStatus) {
    return axios
      .put(
        `${appSettings.endpoints.claims.submit}/${guid}/replacementstatus`,
        replacementStatus,
        {
          headers: this.getBaseHeaders(guid, code)
        })
      .then((results) => { return results })
  },

  /**
   * POST luggage address confirmation object for the claim
   * @param {string} guid - claim's unique id
   * @param {LuggageAddressUpdate} address - address confirmation object
   */
  updateClaimLuggageAddress (guid, address) {
    return axios
      .put(
        `${appSettings.endpoints.claims.submit}/${guid}/luggageaddress`,
        address,
        {
          headers: this.getBaseHeaders()
        })
      .then((results) => { return results })
  },

  /**
   * Returns base headers for the axios requests
   */
  getBaseHeaders (guid, code) {
    let headers = {
      'Accept': 'application/json;version=3',
      'Application-Author': 'first-flight',
      'Application-Name': 'first-flight-claims',
      'Application-Platform': 'web',
      'Application-Version': '2.0'
    }
    if (guid) {
      headers['DamagedLuggage-ClaimIdentifier'] = guid
    }
    if (code) {
      headers['DamagedLuggage-VerificationCode'] = code
    }
    return headers
  }
}

/**
 * Internal model class to hold sumbission result
 */
export class ClaimResult {
  /**
   * @param {string} id
   * todo document add add keys we'd like to translate from external api to local model as needed
   */
  constructor (data) {
    this.id = data.id
  }
}

/**
 * Class to model the response from the messages api
 */
export class ClaimMessage {
  /**
   * Creates a message from object sent from api
   * @param {*} data - options sent from api
   */
  constructor (data) {
    this.isClient = data.isClient
    this.text = data.text
    this.date = data.createdDate
    this.documentId = data.documentId
    this.documentName = data.documentName
    this.documentUrl = data.documentUrl
  }
}
